import React, { useState, useEffect, useMemo } from 'react';
import { 
  TrendingUp, 
  ShoppingCart, 
  ArrowUpRight, 
  ArrowDownRight, 
  Calendar, 
  DollarSign, 
  BarChart2, 
  PieChart, 
  Filter, 
  RefreshCw,
  Users,
  CreditCard,
  Clock,
  Eye
} from 'lucide-react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Select from '../components/ui/Select';
import Input from '../components/ui/Input';
import MetricCard from '../components/ui/MetricCard';
import { useAppStore } from '../store';
import { formatCurrency } from '../utils/formatters';
import { formatArgentinaDate } from '../utils/dateUtils';
import SalesDetailModal from '../components/modals/SalesDetailModal';
import PurchasesDetailModal from '../components/modals/PurchasesDetailModal';
import ReceivablesDetailModal from '../components/modals/ReceivablesDetailModal';
import PayablesDetailModal from '../components/modals/PayablesDetailModal';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  Title, 
  Tooltip, 
  Legend, 
  ArcElement,
  PointElement,
  LineElement,
  Filler
} from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';
import { 
  subMonths, 
  subQuarters, 
  subYears, 
  startOfMonth, 
  startOfQuarter, 
  startOfYear, 
  isAfter,
  format,
  parseISO,
  addDays,
  eachDayOfInterval,
  isSameDay,
  endOfMonth,
  endOfQuarter,
  endOfYear
} from 'date-fns';
import { es } from 'date-fns/locale';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend
);

// ── helpers Express ───────────────────────────────────────────
const getExpressAmount = (o: any) => o.express_value ?? o.total ?? 0;
const getExpressSampleAmount = (s: any) => s.sample_value ?? s.total ?? 0;
// Devuelve la fecha real del pedido Express (soporta ambas columnas)
const getExpressDate = (o: any) => new Date(o.date ?? o.created_at);
// Devuelve true si d está EN o ENTRE start y end (inclusive)
const inRange = (d: Date, start: Date, end: Date) =>
  d >= start && d <= end;

// ── helpers para cálculo sin IVA ──────────────────────────────
// Calcula el monto sin IVA para facturas tipo "A"
const getInvoiceAmountWithoutVAT = (invoice: any) => {
  if (invoice.type === 'A') {
    // Para facturas tipo A, usar subtotal (sin IVA)
    return invoice.subtotal || 0;
  }
  // Para otros tipos, usar total (con IVA)
  return invoice.total || 0;
};

// Calcula el monto sin IVA para gastos
const getExpenseAmountWithoutVAT = (expense: any) => {
  // Para gastos, siempre usar el monto sin IVA
  return (expense.amount || 0) - (expense.vat_amount || 0);
};

// ──────────────────────────────────────────────────────────────

const Dashboard: React.FC = () => {
  const { 
    invoices, 
    payments, 
    clients, 
    suppliers, 
    productions,
    samples,
    express,
    expressSamples,
    expenses,
    darkMode 
  } = useAppStore();
  
  const [period, setPeriod] = useState<'month' | 'month-1' | 'month-2' | 'month-3' | 'month-4' | 'quarter' | 'year' | 'custom'>('month');
  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [isSalesDetailModalOpen, setIsSalesDetailModalOpen] = useState(false);
  const [isPurchasesDetailModalOpen, setIsPurchasesDetailModalOpen] = useState(false);
  const [isReceivablesDetailModalOpen, setIsReceivablesDetailModalOpen] = useState(false);
  const [isPayablesDetailModalOpen, setIsPayablesDetailModalOpen] = useState(false);
  
  // Calculate period start date based on selected period
  const periodStartDate = useMemo(() => {
    const now = new Date();
    
    switch (period) {
      case 'month':
        return startOfMonth(now);
      case 'month-1':
        return startOfMonth(subMonths(now, 1));
      case 'month-2':
        return startOfMonth(subMonths(now, 2));
      case 'month-3':
        return startOfMonth(subMonths(now, 3));
      case 'month-4':
        return startOfMonth(subMonths(now, 4));
      case 'quarter':
        return startOfQuarter(now);
      case 'year':
        return startOfYear(now);
      case 'custom':
        return startDate ? new Date(startDate) : startOfMonth(now);
      default:
        return startOfMonth(now);
    }
  }, [period, startDate]);
  
const periodEndDate = useMemo(() => {
  const now = new Date();

  switch (period) {
    case 'month':
      return endOfMonth(now);                       // ⬅️  antes era "now"
    case 'month-1':
      return endOfMonth(subMonths(now, 1));
    case 'month-2':
      return endOfMonth(subMonths(now, 2));
    case 'month-3':
      return endOfMonth(subMonths(now, 3));
    case 'month-4':
      return endOfMonth(subMonths(now, 4));
    case 'quarter':
      return endOfQuarter(now);                     // ⬅️  cierre de trimestre
    case 'year':
      return endOfYear(now);                        // ⬅️  31-dic
    case 'custom':
      return endDate ? new Date(endDate) : now;
    default:
      return now;
  }
}, [period, endDate]);
  
  // Calculate previous period start date
  const previousPeriodStartDate = useMemo(() => {
    switch (period) {
      case 'month':
        return startOfMonth(subMonths(periodStartDate, 1));
      case 'month-1':
        return startOfMonth(subMonths(periodStartDate, 1));
      case 'month-2':
        return startOfMonth(subMonths(periodStartDate, 1));
      case 'month-3':
        return startOfMonth(subMonths(periodStartDate, 1));
      case 'month-4':
        return startOfMonth(subMonths(periodStartDate, 1));
      case 'quarter':
        return startOfQuarter(subQuarters(periodStartDate, 1));
      case 'year':
        return startOfYear(subYears(periodStartDate, 1));
      case 'custom':
        // For custom period, calculate an equivalent previous period
        const customDuration = periodEndDate.getTime() - periodStartDate.getTime();
        const prevEndDate = new Date(periodStartDate.getTime() - 1);
        const prevStartDate = new Date(prevEndDate.getTime() - customDuration);
        return prevStartDate;
      default:
        return startOfMonth(subMonths(periodStartDate, 1));
    }
  }, [period, periodStartDate, periodEndDate]);
  
const previousPeriodEndDate = useMemo(() => {
  switch (period) {
    case 'month':
      return endOfMonth(subMonths(new Date(), 1));          // ⬅️  mes -1
    case 'month-1':
      return endOfMonth(subMonths(periodEndDate, 1));
    case 'month-2':
      return endOfMonth(subMonths(periodEndDate, 1));
    case 'month-3':
      return endOfMonth(subMonths(periodEndDate, 1));
    case 'month-4':
      return endOfMonth(subMonths(periodEndDate, 1));
    case 'quarter':
      return endOfQuarter(subQuarters(new Date(), 1));
    case 'year':
      return endOfYear(subYears(new Date(), 1));
    case 'custom':
      return new Date(periodStartDate.getTime() - 1);
    default:
      return endOfMonth(subMonths(new Date(), 1));
  }
}, [period, periodStartDate, periodEndDate]);
// ───────────────────────────────────────────────
  
  // Set default date range when component mounts or period changes
  useEffect(() => {
    if (period !== 'custom') {
      setStartDate(format(periodStartDate, 'yyyy-MM-dd'));
      setEndDate(format(periodEndDate, 'yyyy-MM-dd'));
    }
  }, [period, periodStartDate, periodEndDate]);
  
  // Filter invoices by period
  const periodInvoices = useMemo(() => {
    return invoices.filter(invoice => {
      const invoiceDate = new Date(invoice.date);
      return isAfter(invoiceDate, periodStartDate) && 
             !isAfter(invoiceDate, periodEndDate) && 
             invoice.entity_type === 'client' && 
             !invoice.voided;
    });
  }, [invoices, periodStartDate, periodEndDate]);
  
  // Filter invoices by previous period
  const previousPeriodInvoices = useMemo(() => {
    return invoices.filter(invoice => {
      const invoiceDate = new Date(invoice.date);
      return isAfter(invoiceDate, previousPeriodStartDate) && 
             !isAfter(invoiceDate, previousPeriodEndDate) && 
             invoice.entity_type === 'client' && 
             !invoice.voided;
    });
  }, [invoices, previousPeriodStartDate, previousPeriodEndDate]);
  
  // Filter supplier invoices by period
  const periodSupplierInvoices = useMemo(() => {
    return invoices.filter(invoice => {
      const invoiceDate = new Date(invoice.date);
      return isAfter(invoiceDate, periodStartDate) && 
             !isAfter(invoiceDate, periodEndDate) && 
             invoice.entity_type === 'supplier' && 
             !invoice.voided;
    });
  }, [invoices, periodStartDate, periodEndDate]);
  
  // Filter supplier invoices by previous period
  const previousPeriodSupplierInvoices = useMemo(() => {
    return invoices.filter(invoice => {
      const invoiceDate = new Date(invoice.date);
      return isAfter(invoiceDate, previousPeriodStartDate) && 
             !isAfter(invoiceDate, previousPeriodEndDate) && 
             invoice.entity_type === 'supplier' && 
             !invoice.voided;
    });
  }, [invoices, previousPeriodStartDate, previousPeriodEndDate]);
  
  // Filter expenses by period
  const periodExpenses = useMemo(() => {
    return expenses.filter(expense => {
      const expenseDate = new Date(expense.date);
      return isAfter(expenseDate, periodStartDate) && 
             !isAfter(expenseDate, periodEndDate);
    });
  }, [expenses, periodStartDate, periodEndDate]);
  
  // Filter expenses by previous period
  const previousPeriodExpenses = useMemo(() => {
    return expenses.filter(expense => {
      const expenseDate = new Date(expense.date);
      return isAfter(expenseDate, previousPeriodStartDate) && 
             !isAfter(expenseDate, previousPeriodEndDate);
    });
  }, [expenses, previousPeriodStartDate, previousPeriodEndDate]);
  
  // Filter productions by period
  const periodProductions = useMemo(() => {
    return productions.filter(production => {
      const productionDate = new Date(production.created_at);
      return isAfter(productionDate, periodStartDate) && 
             !isAfter(productionDate, periodEndDate);
    });
  }, [productions, periodStartDate, periodEndDate]);
  
  // Filter productions by previous period
  const previousPeriodProductions = useMemo(() => {
    return productions.filter(production => {
      const productionDate = new Date(production.created_at);
      return isAfter(productionDate, previousPeriodStartDate) && 
             !isAfter(productionDate, previousPeriodEndDate);
    });
  }, [productions, previousPeriodStartDate, previousPeriodEndDate]);
  
  // Filter samples by period
  const periodSamples = useMemo(() => {
    return samples.filter(sample => {
      const sampleDate = new Date(sample.created_at);
      return isAfter(sampleDate, periodStartDate) && 
             !isAfter(sampleDate, periodEndDate);
    });
  }, [samples, periodStartDate, periodEndDate]);
  
  // Filter samples by previous period
  const previousPeriodSamples = useMemo(() => {
    return samples.filter(sample => {
      const sampleDate = new Date(sample.created_at);
      return isAfter(sampleDate, previousPeriodStartDate) && 
             !isAfter(sampleDate, previousPeriodEndDate);
    });
  }, [samples, previousPeriodStartDate, previousPeriodEndDate]);
  
const periodExpress = express.filter(o => {
  const d = getExpressDate(o);
  return d >= periodStartDate && d <= periodEndDate;
});

// =========================================

// ===== NUEVO previousPeriodExpress =====
const previousPeriodExpress = express.filter(o => {
  const d = getExpressDate(o);
  return d >= previousPeriodStartDate && d <= previousPeriodEndDate;
});

// =======================================

// ===== NUEVO periodExpressSamples =====
const periodExpressSamples = expressSamples.filter(sp => {
  const d = getExpressDate(sp);
  return d >= periodStartDate && d <= periodEndDate;
});

// =======================================

// == NUEVO previousPeriodExpressSamples ==
const previousPeriodExpressSamples = expressSamples.filter(sp => {
  const d = getExpressDate(sp);
  return d >= previousPeriodStartDate && d <= previousPeriodEndDate;
});

// ========================================

const totalSales =
  periodInvoices.reduce((s, i) => s + getInvoiceAmountWithoutVAT(i), 0) +
  periodExpress.reduce((s, o) => s + getExpressAmount(o), 0) +
  periodExpressSamples.reduce((s, sp) => s + getExpressSampleAmount(sp), 0) +
  periodSamples.reduce((s, sample) => s + (sample.sample_value || 0), 0);


// --------------------------------------------------------------
  
  // Prepare detailed sales data for the modal
const detailedSalesData = useMemo(() => {
  const invoiceSales       = periodInvoices.reduce((s, i) => s + getInvoiceAmountWithoutVAT(i), 0);
  const productionSales    = periodProductions.reduce((s, p) => s + (p.production_value || 0), 0);
  const sampleSales        = periodSamples.reduce((s, sp) => s + (sp.sample_value || 0), 0);
  const expressSales       = periodExpress.reduce((s, o) => s + getExpressAmount(o), 0);
  const expressSampleSales = periodExpressSamples.reduce((s, sp) => s + (sp.sample_value || 0), 0);

  return {
    totalSales,
    invoiceSales,
    productionSales,
    sampleSales,
    expressSales,
    expressSampleSales
  };
}, [totalSales, periodInvoices, periodProductions, periodSamples, periodExpress, periodExpressSamples]);
// --------------------------------------------------------------
const previousTotalSales =
  previousPeriodInvoices.reduce((s, i) => s + getInvoiceAmountWithoutVAT(i), 0) +
  previousPeriodExpress.reduce((s, o) => s + getExpressAmount(o), 0) +
  previousPeriodExpressSamples.reduce((s, sp) => s + getExpressSampleAmount(sp), 0) +
  previousPeriodSamples.reduce((s, sample) => s + (sample.sample_value || 0), 0);
  
  // Calculate sales change percentage
  const salesChangePercentage = useMemo(() => {
    if (previousTotalSales === 0) return 100;
    return Math.round(((totalSales - previousTotalSales) / previousTotalSales) * 100);
  }, [totalSales, previousTotalSales]);
  
  // Calculate total purchases for current period (including expenses)
  const totalPurchases = useMemo(() => {
    const supplierInvoicesTotal = periodSupplierInvoices.reduce((sum, invoice) => sum + getInvoiceAmountWithoutVAT(invoice), 0);
    const expensesTotal = periodExpenses.reduce((sum, expense) => sum + getExpenseAmountWithoutVAT(expense), 0);
    return supplierInvoicesTotal + expensesTotal;
  }, [periodSupplierInvoices, periodExpenses]);
  
  // Prepare detailed purchases data for the modal
  const detailedPurchasesData = useMemo(() => {
    const supplierInvoicesTotal = periodSupplierInvoices.reduce((sum, invoice) => sum + getInvoiceAmountWithoutVAT(invoice), 0);
    const expensesTotal = periodExpenses.reduce((sum, expense) => sum + getExpenseAmountWithoutVAT(expense), 0);
    
    return {
      totalPurchases,
      supplierInvoicesTotal,
      expensesTotal
    };
  }, [totalPurchases, periodSupplierInvoices, periodExpenses]);
  
  // Calculate total purchases for previous period (including expenses)
  const previousTotalPurchases = useMemo(() => {
    const supplierInvoicesTotal = previousPeriodSupplierInvoices.reduce((sum, invoice) => sum + getInvoiceAmountWithoutVAT(invoice), 0);
    const expensesTotal = previousPeriodExpenses.reduce((sum, expense) => sum + getExpenseAmountWithoutVAT(expense), 0);
    return supplierInvoicesTotal + expensesTotal;
  }, [previousPeriodSupplierInvoices, previousPeriodExpenses]);
  
  // Calculate purchases change percentage
  const purchasesChangePercentage = useMemo(() => {
    if (previousTotalPurchases === 0) return 100;
    return Math.round(((totalPurchases - previousTotalPurchases) / previousTotalPurchases) * 100);
  }, [totalPurchases, previousTotalPurchases]);
  
  // Calculate total accounts receivable
  const totalReceivable = useMemo(() => {
    return clients.reduce((sum, client) => sum + (client.balance > 0 ? client.balance : 0), 0);
  }, [clients]);
  
  // Calculate total accounts payable
  const totalPayable = useMemo(() => {
    return suppliers.reduce((sum, supplier) => sum + (supplier.balance > 0 ? supplier.balance : 0), 0);
  }, [suppliers]);
  
  // Calculate gross margin
  const grossMargin = useMemo(() => {
    return totalSales - totalPurchases;
  }, [totalSales, totalPurchases]);
  
  // Calculate previous gross margin
  const previousGrossMargin = useMemo(() => {
    return previousTotalSales - previousTotalPurchases;
  }, [previousTotalSales, previousTotalPurchases]);
  
  // Calculate gross margin change percentage
  const grossMarginChangePercentage = useMemo(() => {
    if (previousGrossMargin === 0) return 100;
    return Math.round(((grossMargin - previousGrossMargin) / Math.abs(previousGrossMargin)) * 100);
  }, [grossMargin, previousGrossMargin]);
  
  // Calculate gross margin percentage
  const grossMarginPercentage = useMemo(() => {
    if (totalSales === 0) return 0;
    return Math.round((grossMargin / totalSales) * 100);
  }, [grossMargin, totalSales]);
  
  // Calculate daily income vs expenses data
  const incomeVsExpensesByDayData = useMemo(() => {
    // Get all dates in the current period
    const dates = eachDayOfInterval({
      start: periodStartDate,
      end: periodEndDate
    });

    // Initialize data structure
    const dailyData: {
      income: { [key: string]: number };
      expenses: { [key: string]: number };
    } = {
      income: {},
      expenses: {}
    };

    // Initialize all dates with zero values
    dates.forEach(date => {
      const dateStr = format(date, 'yyyy-MM-dd');
      dailyData.income[dateStr] = 0;
      dailyData.expenses[dateStr] = 0;
    });

    // Add income from invoices
    periodInvoices.forEach(invoice => {
      const dateStr = format(new Date(invoice.date), 'yyyy-MM-dd');
      if (dailyData.income[dateStr] !== undefined) {
        dailyData.income[dateStr] += getInvoiceAmountWithoutVAT(invoice);
      }
    });

    // Add expenses from supplier invoices and general expenses
    periodSupplierInvoices.forEach(invoice => {
      const dateStr = format(new Date(invoice.date), 'yyyy-MM-dd');
      if (dailyData.expenses[dateStr] !== undefined) {
        dailyData.expenses[dateStr] += getInvoiceAmountWithoutVAT(invoice);
      }
    });

    periodExpenses.forEach(expense => {
      const dateStr = format(new Date(expense.date), 'yyyy-MM-dd');
      if (dailyData.expenses[dateStr] !== undefined) {
        dailyData.expenses[dateStr] += getExpenseAmountWithoutVAT(expense);
      }
    });

    // Prepare data for Chart.js
    return {
      labels: dates.map(date => format(date, 'dd/MM/yyyy')),
      datasets: [
        {
          label: 'Ingresos',
          data: dates.map(date => dailyData.income[format(date, 'yyyy-MM-dd')]),
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          fill: true,
          tension: 0.4
        },
        {
          label: 'Gastos',
          data: dates.map(date => dailyData.expenses[format(date, 'yyyy-MM-dd')]),
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          fill: true,
          tension: 0.4
        }
      ]
    };
  }, [periodStartDate, periodEndDate, periodInvoices, periodSupplierInvoices, periodExpenses]);
  
  // Prepare data for sales by client chart
  const salesByClientData = useMemo(() => {
    const clientSales: Record<string, number> = {};
    
    // Add invoice sales
    periodInvoices.forEach(invoice => {
      if (invoice.entity_id) { // Add null check for entity_id
        const client = clients.find(c => c.id === invoice.entity_id);
        if (client?.name) { // Add null check for client and client.name
          clientSales[client.name] = (clientSales[client.name] || 0) + getInvoiceAmountWithoutVAT(invoice);
        }
      }
    });

// — dentro de salesByClientData —
periodExpress.forEach(o => {
  if (o.client_id) {
    const client = clients.find(c => c.id === o.client_id);
    if (client?.name) {
      clientSales[client.name] = (clientSales[client.name] || 0) + getExpressAmount(o);
    }
  }
});
    
    // Add production sales
    periodProductions.forEach(production => {
      if (production.client_id) { // Add null check for client_id
        const client = clients.find(c => c.id === production.client_id);
        if (client?.name) { // Add null check for client and client.name
          clientSales[client.name] = (clientSales[client.name] || 0) + (production.production_value || 0);
        }
      }
    });
    
    // Add sample sales
    periodSamples.forEach(sample => {
      if (sample.client_id) { // Add null check for client_id
        const client = clients.find(c => c.id === sample.client_id);
        if (client?.name) { // Add null check for client and client.name
          clientSales[client.name] = (clientSales[client.name] || 0) + (sample.sample_value || 0);
        }
      }
    });
    
    // Add express sales
    periodExpress.forEach(order => {
      if (order.client_id) { // Add null check for client_id
        const client = clients.find(c => c.id === order.client_id);
        if (client?.name) { // Add null check for client and client.name
          clientSales[client.name] = (clientSales[client.name] || 0) + (order.express_value || 0);
        }
      }
    });
    
    // Add express sample sales
    periodExpressSamples.forEach(sample => {
      if (sample.client_id) { // Add null check for client_id
        const client = clients.find(c => c.id === sample.client_id);
        if (client?.name) { // Add null check for client and client.name
          clientSales[client.name] = (clientSales[client.name] || 0) + (sample.sample_value || 0);
        }
      }
    });
    
    // Convert to array and sort by sales amount
    const sortedSales = Object.entries(clientSales)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5); // Top 5 clients
    
    return {
      labels: sortedSales.map(([client]) => client),
      datasets: [
        {
          label: 'Ventas',
          data: sortedSales.map(([, amount]) => amount),
          backgroundColor: [
            'rgba(99, 102, 241, 0.8)',
            'rgba(79, 70, 229, 0.8)',
            'rgba(67, 56, 202, 0.8)',
            'rgba(55, 48, 163, 0.8)',
            'rgba(49, 46, 129, 0.8)',
          ],
          borderColor: [
            'rgba(99, 102, 241, 1)',
            'rgba(79, 70, 229, 1)',
            'rgba(67, 56, 202, 1)',
            'rgba(55, 48, 163, 1)',
            'rgba(49, 46, 129, 1)',
          ],
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
    };
  }, [periodInvoices, periodProductions, periodSamples, periodExpress, periodExpressSamples, clients]);
  
  // Prepare data for purchases by supplier chart
  const purchasesBySupplierData = useMemo(() => {
    const supplierPurchases: Record<string, number> = {};
    
    periodSupplierInvoices.forEach(invoice => {
      if (invoice.entity_id) { // Add null check for entity_id
        const supplier = suppliers.find(s => s.id === invoice.entity_id);
        if (supplier?.name) { // Add null check for supplier and supplier.name
          supplierPurchases[supplier.name] = (supplierPurchases[supplier.name] || 0) + getInvoiceAmountWithoutVAT(invoice);
        }
      }
    });
    
    // Add expenses by supplier
    periodExpenses.forEach(expense => {
      if (expense.supplier_id) {
        const supplier = suppliers.find(s => s.id === expense.supplier_id);
        if (supplier?.name) { // Add null check for supplier and supplier.name
          supplierPurchases[supplier.name] = (supplierPurchases[supplier.name] || 0) + getExpenseAmountWithoutVAT(expense);
        } else {
          // For expenses without supplier, group as "Otros gastos"
          supplierPurchases["Otros gastos"] = (supplierPurchases["Otros gastos"] || 0) + getExpenseAmountWithoutVAT(expense);
        }
      } else {
        // For expenses without supplier, group as "Otros gastos"
        supplierPurchases["Otros gastos"] = (supplierPurchases["Otros gastos"] || 0) + getExpenseAmountWithoutVAT(expense);
      }
    });
    
    // Convert to array and sort by purchase amount
    const sortedPurchases = Object.entries(supplierPurchases)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5); // Top 5 suppliers
    
    return {
      labels: sortedPurchases.map(([supplier]) => supplier),
      datasets: [
        {
          label: 'Compras',
          data: sortedPurchases.map(([, amount]) => amount),
          backgroundColor: [
            'rgba(239, 68, 68, 0.8)',
            'rgba(249, 115, 22, 0.8)',
            'rgba(245, 158, 11, 0.8)',
            'rgba(234, 179, 8, 0.8)',
            'rgba(132, 204, 22, 0.8)',
          ],
          borderColor: [
            'rgba(239, 68, 68, 1)',
            'rgba(249, 115, 22, 1)',
            'rgba(245, 158, 11, 1)',
            'rgba(234, 179, 8, 1)',
            'rgba(132, 204, 22, 1)',
          ],
          borderWidth: 1,
          hoverOffset: 15,
        },
      ],
    };
  }, [periodSupplierInvoices, periodExpenses, suppliers]);
  
  // Prepare data for top accounts receivable
  const topAccountsReceivable = useMemo(() => {
    return clients
      .filter(client => client.balance > 0)
      .sort((a, b) => b.balance - a.balance)
      .slice(0, 5);
  }, [clients]);
  
  // Prepare data for top accounts payable
  const topAccountsPayable = useMemo(() => {
    return suppliers
      .filter(supplier => supplier.balance > 0)
      .sort((a, b) => b.balance - a.balance)
      .slice(0, 5);
  }, [suppliers]);
  
const recentTransactions = useMemo(() => {
  const allTransactions = [
    // ← FACTURAS
    ...invoices.map(inv => ({
      id:          inv.id,
      date:        inv.date,
      timestamp:   new Date(inv.date).getTime(),          // para ordenar por fecha
      createdAt:   new Date(inv.created_at).getTime(),    // para desempate en mismo día
      description: `Factura ${inv.number}`,
      entity:      inv.entity_type === 'client'
                     ? clients.find(c => c.id === inv.entity_id)?.name || 'Cliente desconocido'
                     : suppliers.find(s => s.id === inv.entity_id)?.name || 'Proveedor desconocido',
      amount:      getInvoiceAmountWithoutVAT(inv),
      type:        inv.entity_type === 'client' ? 'income' : 'expense'
    })),

    // ← PAGOS
    ...payments.map(pay => {
      const inv = invoices.find(i => i.id === pay.invoice_id);
      return {
        id:          pay.id,
        date:        pay.date,
        timestamp:   new Date(pay.date).getTime(),
        createdAt:   new Date(pay.created_at).getTime(),
        description: `Pago ${pay.method}`,
        entity:      inv
                      ? inv.entity_type === 'client'
                        ? clients.find(c => c.id === inv.entity_id)?.name || 'Cliente desconocido'
                        : suppliers.find(s => s.id === inv.entity_id)?.name || 'Proveedor desconocido'
                      : 'Factura desconocida',
        amount:      pay.amount,
        type:        inv?.entity_type === 'client' ? 'payment_received' : 'payment_made'
      };
    }),

    // ← GASTOS
    ...expenses.map(exp => ({
      id:          exp.id,
      date:        exp.date,
      timestamp:   new Date(exp.date).getTime(),
      createdAt:   exp.created_at ? new Date(exp.created_at).getTime() : 0,
      description: exp.description,
      entity:      exp.supplier_id
                     ? suppliers.find(s => s.id === exp.supplier_id)?.name || 'Proveedor desconocido'
                     : 'Sin proveedor',
      amount:      getExpenseAmountWithoutVAT(exp),
      type:        'expense'
    }))
  ];

  // Orden: más nuevo primero (fecha) y, si empatan, el creado más reciente
  return allTransactions
    .sort((a, b) => {
      if (b.timestamp !== a.timestamp) return b.timestamp - a.timestamp;
      return (b.createdAt ?? 0) - (a.createdAt ?? 0);
    })
    .slice(0, 10);   // últimos 10
}, [invoices, payments, expenses, clients, suppliers]);

  
  // Prepare data for sales by salesperson
  const salesBySalesperson = useMemo(() => {
    const salesData: Record<string, {
      invoices: number; 
      productions: number;
      samples: number;
      express: number;
      expressSamples: number;
      total: number;
      commission: number;
    }> = {
      'Mateo': {
        invoices: 0,
        productions: 0,
        samples: 0,
        express: 0,
        expressSamples: 0,
        total: 0,
        commission: 0
      },
      'Male': {
        invoices: 0,
        productions: 0,
        samples: 0,
        express: 0,
        expressSamples: 0,
        total: 0,
        commission: 0
      }
    };
    
    // Add invoice sales by salesperson
    periodInvoices.forEach(invoice => {
      if (invoice.salesperson && salesData[invoice.salesperson]) {
        salesData[invoice.salesperson].invoices += getInvoiceAmountWithoutVAT(invoice);
        salesData[invoice.salesperson].total += getInvoiceAmountWithoutVAT(invoice);
      }
    });
    
    // Add express sales by salesperson
periodExpress.forEach(o => {
  if (o.salesperson && salesData[o.salesperson]) {
    const amt = getExpressAmount(o);
    salesData[o.salesperson].express += amt;
    salesData[o.salesperson].total   += amt;
  }
});

    
    // Calculate commission (2%)
    Object.keys(salesData).forEach(salesperson => {
      salesData[salesperson].commission = salesData[salesperson].total * 0.02;
    });
    
    return salesData;
  }, [periodInvoices, periodExpress]);
  
  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: darkMode ? '#e0e0e0' : '#333',
          font: {
            family: "'Inter', sans-serif",
            size: 12
          },
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle'
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: darkMode ? 'rgba(30, 41, 59, 0.9)' : 'rgba(255, 255, 255, 0.9)',
        titleColor: darkMode ? '#e0e0e0' : '#333',
        bodyColor: darkMode ? '#e0e0e0' : '#333',
        borderColor: darkMode ? 'rgba(55, 65, 81, 0.3)' : 'rgba(0, 0, 0, 0.1)',
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        titleFont: {
          family: "'Inter', sans-serif",
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          family: "'Inter', sans-serif",
          size: 13
        },
        callbacks: {
          label: function(context: any) {
            return `${context.dataset.label}: ${formatCurrency(context.raw)}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value: number) => formatCurrency(value),
          color: darkMode ? '#e0e0e0' : '#333',
          font: {
            family: "'Inter', sans-serif",
            size: 11
          }
        },
        grid: {
          color: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
          drawBorder: false
        }
      },
      x: {
        ticks: {
          color: darkMode ? '#e0e0e0' : '#333',
          font: {
            family: "'Inter', sans-serif",
            size: 11
          }
        },
        grid: {
          display: false
        }
      }
    },
    elements: {
      bar: {
        borderRadius: 4
      }
    }
  };
  
  // Pie chart options
  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          color: darkMode ? '#e0e0e0' : '#333',
          font: {
            family: "'Inter', sans-serif",
            size: 12
          },
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle'
        },
      },
      tooltip: {
        backgroundColor: darkMode ? 'rgba(30, 41, 59, 0.9)' : 'rgba(255, 255, 255, 0.9)',
        titleColor: darkMode ? '#e0e0e0' : '#333',
        bodyColor: darkMode ? '#e0e0e0' : '#333',
        borderColor: darkMode ? 'rgba(55, 65, 81, 0.3)' : 'rgba(0, 0, 0, 0.1)',
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        titleFont: {
          family: "'Inter', sans-serif",
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          family: "'Inter', sans-serif",
          size: 13
        },
        callbacks: {
          label: function(context: any) {
            const label = context.label || '';
            const value = formatCurrency(context.raw);
            const percentage = Math.round(context.raw * 100 / context.dataset.data.reduce((a: number, b: number) => a + b, 0)) + '%';
            return `${label}: ${value} (${percentage})`;
          }
        }
      }
    },
    cutout: '50%',
    radius: '90%'
  };
  
  // Line chart options
  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: darkMode ? '#e0e0e0' : '#333',
          font: {
            family: "'Inter', sans-serif",
            size: 12
          },
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle'
        },
      },
      tooltip: {
        backgroundColor: darkMode ? 'rgba(30, 41, 59, 0.9)' : 'rgba(255, 255, 255, 0.9)',
        titleColor: darkMode ? '#e0e0e0' : '#333',
        bodyColor: darkMode ? '#e0e0e0' : '#333',
        borderColor: darkMode ? 'rgba(55, 65, 81, 0.3)' : 'rgba(0, 0, 0, 0.1)',
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        titleFont: {
          family: "'Inter', sans-serif",
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          family: "'Inter', sans-serif",
          size: 13
        },
        callbacks: {
          label: function(context: any) {
            return `${context.dataset.label}: ${formatCurrency(context.raw)}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value: number) => formatCurrency(value),
          color: darkMode ? '#e0e0e0' : '#333',
          font: {
            family: "'Inter', sans-serif",
            size: 11
          }
        },
        grid: {
          color: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
          drawBorder: false
        }
      },
      x: {
        ticks: {
          color: darkMode ? '#e0e0e0' : '#333',
          font: {
            family: "'Inter', sans-serif",
            size: 11
          }
        },
        grid: {
          display: false
        }
      }
    },
    elements: {
      line: {
        tension: 0.4
      },
      point: {
        radius: 3,
        hoverRadius: 5
      }
    }
  };
  
  // Refresh data
  const handleRefresh = () => {
    setIsLoading(true);
    
    // Simulate loading
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Dashboard</h2>
        <div className="flex gap-2">
          <Select
            label=""
            value={period}
            onChange={(value) => setPeriod(value as typeof period)}
            options={[
              { value: 'month', label: 'Este mes' },
              { value: 'month-1', label: 'Mes anterior' },
              { value: 'month-2', label: 'Hace 2 meses' },
              { value: 'month-3', label: 'Hace 3 meses' },
              { value: 'month-4', label: 'Hace 4 meses' },
              { value: 'quarter', label: 'Este trimestre' },
              { value: 'year', label: 'Este año' },
              { value: 'custom', label: 'Personalizado' },
            ]}
          />
        </div>
      </div>
      
      {period === 'custom' && (
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Fecha Inicial"
                type="date"
                id="startDate"
                name="startDate"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <Input
                label="Fecha Final"
                type="date"
                id="endDate"
                name="endDate"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          label={
            <div className="flex items-center justify-between w-full">
              <span>Ventas del periodo</span>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setIsSalesDetailModalOpen(true);
                }}
                className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 focus:outline-none"
                title="Ver detalle"
              >
                <Eye className="h-4 w-4" />
              </button>
            </div>
          }
          value={totalSales}
          changePercentage={salesChangePercentage}
          icon="TrendingUp"
        />
        <MetricCard
          label={
            <div className="flex items-center justify-between w-full">
              <span>Compras del periodo</span>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setIsPurchasesDetailModalOpen(true);
                }}
                className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 focus:outline-none"
                title="Ver detalle"
              >
                <Eye className="h-4 w-4" />
              </button>
            </div>
          }
          value={totalPurchases}
          changePercentage={purchasesChangePercentage}
          icon="ShoppingCart"
        />
        <MetricCard
          label={
            <div className="flex items-center justify-between w-full">
              <span>Cuentas por cobrar</span>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setIsReceivablesDetailModalOpen(true);
                }}
                className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 focus:outline-none"
                title="Ver detalle"
              >
                <Eye className="h-4 w-4" />
              </button>
            </div>
          }
          value={totalReceivable}
          icon="ArrowUpRight"
        />
        <MetricCard
          label={
            <div className="flex items-center justify-between w-full">
              <span>Cuentas por pagar</span>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setIsPayablesDetailModalOpen(true);
                }}
                className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 focus:outline-none"
                title="Ver detalle"
              >
                <Eye className="h-4 w-4" />
              </button>
            </div>
          }
          value={totalPayable}
          icon="ArrowDownRight"
        />
      </div>
      
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader title="Ventas por cliente" />
          <CardContent>
            <div className="h-80">
              {isLoading ? (
                <div className="h-full flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
                </div>
              ) : salesByClientData.labels.length > 0 ? (
                <Bar options={chartOptions} data={salesByClientData} />
              ) : (
                <div className="h-full flex items-center justify-center">
                  <p className="text-gray-500 dark:text-gray-400">No hay datos disponibles</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader title="Compras por proveedor" />
          <CardContent>
            <div className="h-80">
              {isLoading ? (
                <div className="h-full flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
                </div>
              ) : purchasesBySupplierData.labels.length > 0 ? (
                <Pie options={pieChartOptions} data={purchasesBySupplierData} />
              ) : (
                <div className="h-full flex items-center justify-center">
                  <p className="text-gray-500 dark:text-gray-400">No hay datos disponibles</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Income vs Expenses Chart */}
      <Card>
        <CardHeader title="Ingresos vs Gastos (Mes Actual)" />
        <CardContent>
          <div className="h-80">
            {isLoading ? (
              <div className="h-full flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
              </div>
            ) : incomeVsExpensesByDayData.labels.length > 0 ? (
              <Line options={lineChartOptions} data={incomeVsExpensesByDayData} />
            ) : (
              <div className="h-full flex items-center justify-center">
                <p className="text-gray-500 dark:text-gray-400">No hay datos disponibles</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Top Accounts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader title="Top Cuentas por Cobrar" />
          <CardContent>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Cliente
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Saldo
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {topAccountsReceivable.length > 0 ? (
                    topAccountsReceivable.map((client) => (
                      <tr key={client.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {client.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-emerald-600 dark:text-emerald-400 font-medium">
                          {formatCurrency(client.balance)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={2} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                        No hay cuentas por cobrar
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader title="Top Cuentas por Pagar" />
          <CardContent>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Proveedor
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Saldo
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {topAccountsPayable.length > 0 ? (
                    topAccountsPayable.map((supplier) => (
                      <tr key={supplier.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {supplier.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-red-600 dark:text-red-400 font-medium">
                          {formatCurrency(supplier.balance)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={2} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                        No hay cuentas por pagar
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Recent Transactions */}
      {/* Salesperson Commissions */}
      <Card>
        <CardHeader title="Comisiones por Vendedor" />
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Vendedor
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Facturas
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Express
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Total Ventas
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Comisión (2%)
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {Object.entries(salesBySalesperson).map(([salesperson, data]) => (
                  <tr key={salesperson} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {salesperson}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                      {formatCurrency(data.invoices)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                      {formatCurrency(data.express)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900 dark:text-white">
                      {formatCurrency(data.total)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-emerald-600 dark:text-emerald-400">
                      {formatCurrency(data.commission)}
                    </td>
                  </tr>
                ))}
                {Object.keys(salesBySalesperson).length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                      No hay datos de comisiones para el período seleccionado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader title="Transacciones Recientes" />
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Descripción
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Entidad
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Monto
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {recentTransactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatArgentinaDate(transaction.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {transaction.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {transaction.entity}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-medium ${
                      transaction.type === 'income' || transaction.type === 'payment_received'
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      {transaction.type === 'income' || transaction.type === 'payment_received' ? '+' : '-'}
                      {formatCurrency(transaction.amount)}
                    </td>
                  </tr>
                ))}
                {recentTransactions.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                      No hay transacciones recientes
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      
      {/* Sales Detail Modal */}
      <SalesDetailModal
        isOpen={isSalesDetailModalOpen}
        onClose={() => setIsSalesDetailModalOpen(false)}
        salesData={detailedSalesData}
        period={period}
      />
      
      {/* Purchases Detail Modal */}
      <PurchasesDetailModal
        isOpen={isPurchasesDetailModalOpen}
        onClose={() => setIsPurchasesDetailModalOpen(false)}
        purchasesData={detailedPurchasesData}
        period={period}
      />
      
      {/* Receivables Detail Modal */}
      <ReceivablesDetailModal
        isOpen={isReceivablesDetailModalOpen}
        onClose={() => setIsReceivablesDetailModalOpen(false)}
        clients={clients}
      />
      
      {/* Payables Detail Modal */}
      <PayablesDetailModal
        isOpen={isPayablesDetailModalOpen}
        onClose={() => setIsPayablesDetailModalOpen(false)}
        suppliers={suppliers}
      />
    </div>
  );
};

export default Dashboard;